import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import { db, collection, query, where, onSnapshot } from '../firebaseConfig';
import 'react-datepicker/dist/react-datepicker.css';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfYear, endOfYear, isWithinInterval } from 'date-fns';
import './JournalTable.css';
import MDBox from '../components/MDBox';
import MDTypography from '../components/MDTypography';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { calculatePipIncrement } from './TradeEntryForm';
import {
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
} from '@mui/material';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import WaterfallChartIcon from '@mui/icons-material/WaterfallChart';
import { useTheme } from '@mui/material/styles';
import Menu from '@mui/material/Menu';

const TeamJournalTable = ({ userID, tradeAccountID = 'default' }) => {
  const navigate = useNavigate();
  const [journalEntries, setJournalEntries] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterBySymbol, setFilterBySymbol] = useState('');
  const [filterByConfidence, setFilterByConfidence] = useState('');
  const [filterByResult, setFilterByResult] = useState('');
  const [filterByDate, setFilterByDate] = useState('');
  const [filterByStatus, setFilterByStatus] = useState('');
  const [filterByTags, setFilterByTags] = useState('');
  const [filterByTradeScore, setFilterByTradeScore] = useState('All');
  const [subFilterByDate, setSubFilterByDate] = useState('');
  const [filterByMonth, setFilterByMonth] = useState('All');
  const [filterByYear, setFilterByYear] = useState('All');
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [selectedTradeURL, setSelectedTradeURL] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const tableRef = useRef(null);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    if (!userID || !tradeAccountID) return;

    const fetchData = async () => {
      try {
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const q = query(collection(db, tradesPath), where('userID', '==', userID));
        const observer = onSnapshot(q, (querySnapshot) => {
          const entries = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            const risk = Math.abs(parseFloat(data.entry) - parseFloat(data.sl));
            const reward = Math.abs(parseFloat(data.tp) - parseFloat(data.entry));
            const riskRewardRatio = risk ? reward / risk : 0;

            const entryDateTime =
              data.entryDateTime && !isNaN(new Date(data.entryDateTime).getTime())
                ? data.entryDateTime
                : data.date && data.entryTime && !isNaN(new Date(`${data.date}T${data.entryTime}`).getTime())
                ? `${data.date}T${data.entryTime}`
                : data.date && !isNaN(new Date(data.date).getTime())
                ? data.date
                : 'N/A';

            const exitDateTime =
              data.exitDateTime && !isNaN(new Date(data.exitDateTime).getTime())
                ? data.exitDateTime
                : data.exitDate && data.exitDate !== '' && !isNaN(new Date(data.exitDate).getTime())
                ? data.exitTime && data.exitTime !== '' ? `${data.exitDate}T${data.exitTime}` : data.exitDate
                : 'N/A';

            let actualRRRatio = 'N/A';
            if (data.status !== 'Open' && data.exit) {
              const actualReward = Math.abs(parseFloat(data.exit) - parseFloat(data.entry));
              const isLoss = data.shortLong === 'Long' ? parseFloat(data.exit) < parseFloat(data.entry) : parseFloat(data.exit) > parseFloat(data.entry);
              actualRRRatio = risk ? (isLoss ? (-1 * (actualReward / risk)).toFixed(2) : (actualReward / risk).toFixed(2)) : 'N/A';
            }

            return {
              ...data,
              id: doc.id,
              date: data.date || 'N/A',
              status: data.status || 'N/A',
              entryTime: data.entryTime || 'N/A',
              exitDate: data.exitDate || 'N/A',
              exitTime: data.exitTime || 'N/A',
              symbol: data.symbol || 'N/A',
              entry: data.entry || 'N/A',
              exit: data.exit || 'N/A',
              returns: data.returns !== null && data.returns !== undefined && !isNaN(data.returns) ? data.returns : 'N/A',
              sl: data.sl || 'N/A',
              tp: data.tp || 'N/A',
              comments: data.comments || 'N/A',
              returnPercentage: parseFloat(data.returnPercentage) || 0,
              riskRewardRatio: parseFloat(riskRewardRatio.toFixed(2)) || 0,
              actualRRRatio,
              entryDateTime,
              exitDateTime,
              fxbTradeScore: data.fxbTradeScore || 'N/A',
              tradeConfidence: data.tradeConfidence || 'N/A',
              commission: data.commission || 0,
            };
          });
          setJournalEntries(entries);
          console.log('Fetched trades for userID:', userID, 'tradeAccountID:', tradeAccountID, 'Count:', entries.length);
        });
        return () => observer();
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [userID, tradeAccountID]);

  const uniqueSymbols = [...new Set(journalEntries.map((entry) => entry.symbol))];
  const allTags = journalEntries.flatMap((entry) => entry.tags || []);
  const uniqueTags = [...new Set(allTags)];
  const uniqueStatus = [...new Set(journalEntries.map((entry) => entry.status))];

  const handleSort = (header) => {
    if (sortBy === header) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(header);
      setSortOrder('asc');
    }
  };

  const closeModal = (event) => {
    if (tableRef.current && event.target.closest('.actions')) {
      return;
    }
    setIsModalOpen(false);
    setSelectedTrade(null);
  };

  const handleRowClick = (entry) => {
    console.log('Setting selectedTrade:', entry);
    setSelectedTrade(entry);
    setSelectedTradeURL(entry.screenshot);
    setIsModalOpen(true);
  };

  const handleFilterBySymbol = (symbol) => {
    setFilterBySymbol(symbol === 'All' ? '' : symbol);
  };

  const handleFilterByTags = (tags) => {
    setFilterByTags(tags === 'All' ? '' : tags);
  };

  const handleFilterByConfidence = (confidence) => {
    setFilterByConfidence(confidence === 'All' ? '' : confidence);
  };

  const handleMenuOpen = (event, entry) => {
    setAnchorEl(event.currentTarget);
    setSelectedTrade(entry);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTrade(null);
  };

  const handleFilterByResult = (result) => {
    setFilterByResult(result === 'All' ? '' : result);
  };

  const handleFilterByDate = (date) => {
    setFilterByDate(date);
  };

  const handleSubFilterByDate = (subFilter) => {
    setSubFilterByDate(subFilter === 'All' ? '' : subFilter);
  };

  const handleFilterByStatus = (status) => {
    setFilterByStatus(status === 'All' ? '' : status);
  };

  const handleFilterByMonth = (value) => {
    console.log('Selected Month:', value);
    setFilterByMonth(value);
  };

  const handleFilterByYear = (value) => {
    console.log('Selected Year:', value);
    setFilterByYear(value);
  };

  const renderTradeScore = (fxbTradeScore) => {
    if (fxbTradeScore === 'N/A') {
      return <MDTypography variant="body3">N/A</MDTypography>;
    }
  
    const [score, rest] = fxbTradeScore.split('/');
    const scoreNum = parseInt(score, 10);
  
    let color;
    if (scoreNum >= 80) color = 'success';
    else if (scoreNum >= 50) color = 'warning';
    else color = 'error';
  
    return (
      <MDTypography variant="body3">
        <span style={{ color: theme.palette[color].main }}>{score}</span>
        <span>/{rest}</span>
      </MDTypography>
    );
  };

  const getConfidenceIcon = (confidence) => {
    if (!confidence) {
      return <MDTypography variant="body3">N/A</MDTypography>;
    }
    switch (confidence) {
      case 'High':
        return <SentimentVerySatisfiedIcon color="success" style={{ fontSize: 30 }} />;
      case 'Medium':
        return <SentimentSatisfiedIcon color="warning" style={{ fontSize: 30 }} />;
      case 'Low':
        return <SentimentDissatisfiedIcon color="error" style={{ fontSize: 30 }} />;
      default:
        return <MDTypography variant="body3">N/A</MDTypography>;
    }
  };

  const calculateHoldTime = (entryDateTime, exitDate, exitTime) => {
    if (!entryDateTime || !exitDate) return 'N/A';

    const entryDate = new Date(entryDateTime);
    const exitDateTime = new Date(exitDate + 'T' + (exitTime || '00:00'));

    const diffMs = exitDateTime - entryDate;
    if (diffMs <= 0) return 'N/A';

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffDays} days ${diffHours} hours ${diffMinutes} minutes`;
  };

  const filteredEntries = () => {
    let filtered = [...journalEntries];

    if (filterBySymbol) {
      filtered = filtered.filter((entry) => entry.symbol === filterBySymbol);
    }

    if (filterByTags) {
      filtered = filtered.filter((entry) => {
        const tags = Array.isArray(entry.tags) ? entry.tags : [];
        return tags.includes(filterByTags);
      });
    }

    if (filterByStatus) {
      filtered = filtered.filter((entry) => entry.status === filterByStatus);
    }

    if (filterByTradeScore && filterByTradeScore !== 'All') {
      filtered = filtered.filter((entry) => {
        const score = entry.fxbTradeScore === 'N/A' ? 0 : parseInt(entry.fxbTradeScore.split('/')[0], 10);
        if (filterByTradeScore === '80-100') return score >= 80;
        if (filterByTradeScore === '50-79') return score >= 50 && score < 80;
        if (filterByTradeScore === '<50') return score < 50;
        return true;
      });
    }

    if (filterByConfidence === 'High') {
      filtered = filtered.filter((entry) => entry.tradeConfidence === 'High');
    } else if (filterByConfidence === 'Medium') {
      filtered = filtered.filter((entry) => entry.tradeConfidence === 'Medium');
    } else if (filterByConfidence === 'Low') {
      filtered = filtered.filter((entry) => entry.tradeConfidence === 'Low');
    }

    if (filterByResult === 'wins') {
      filtered = filtered.filter((entry) => entry.returns >= 0);
    } else if (filterByResult === 'losses') {
      filtered = filtered.filter((entry) => entry.returns < 0);
    }

    if (filterByDate) {
      filtered = filtered.filter((entry) => {
        const entryDate = new Date(entry.date);
        const filterDate = new Date(filterByDate);
        return entryDate.toDateString() === filterDate.toDateString();
      });
    }

    const today = new Date();
    if (subFilterByDate === 'today') {
      filtered = filtered.filter((entry) => {
        const entryDate = new Date(entry.date);
        return entryDate.toDateString() === today.toDateString();
      });
    } else if (subFilterByDate === 'week') {
      const startOfWeekDate = startOfWeek(today);
      const endOfWeekDate = endOfWeek(today);
      filtered = filtered.filter((entry) => isWithinInterval(new Date(entry.date), { start: startOfWeekDate, end: endOfWeekDate }));
    } else if (subFilterByDate === 'month') {
      const startDate = startOfMonth(today);
      const endDate = endOfMonth(today);
      filtered = filtered.filter((entry) => isWithinInterval(new Date(entry.date), { start: startDate, end: endDate }));
    } else if (subFilterByDate === 'year') {
      const startOfYearDate = startOfYear(today);
      const endOfYearDate = endOfYear(today);
      filtered = filtered.filter((entry) => isWithinInterval(new Date(entry.date), { start: startOfYearDate, end: endOfYearDate }));
    }

    if (filterByMonth !== 'All' || filterByYear !== 'All') {
      filtered = filtered.filter((entry) => {
        const entryDate = new Date(entry.date);
        const entryMonth = entryDate.getUTCMonth() + 1;
        const entryYear = entryDate.getUTCFullYear();
        return (
          (filterByMonth === 'All' || entryMonth === parseInt(filterByMonth)) &&
          (filterByYear === 'All' || entryYear === parseInt(filterByYear))
        );
      });
    }

    return filtered;
  };

  const sortedEntries = () => {
    if (!sortBy) {
      return filteredEntries();
    }

    const multiplier = sortOrder === 'asc' ? 1 : -1;

    return filteredEntries().sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'fxbTradeScore') {
        aValue = aValue === 'N/A' ? -Infinity : parseInt(aValue.split('/')[0], 10);
        bValue = bValue === 'N/A' ? -Infinity : parseInt(bValue.split('/')[0], 10);
        return (aValue - bValue) * multiplier;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * multiplier;
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * multiplier;
      } else {
        return 0;
      }
    });
  };

  const renderSortIndicator = (header) => {
    if (sortBy === header) {
      return sortOrder === 'asc' ? '▲' : '▼';
    }
    return '';
  };

  const isReplayAvailable = (trade) => {
    if (!trade || !trade.exitDateTime) return false;
    const exitTime = new Date(trade.exitDateTime).getTime();
    const now = Date.now();
    return now - exitTime >= 24 * 60 * 60 * 1000; // 24 hours
  };

  const getYearsArray = (startYear, endYear) => {
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  const currentYear = new Date().getFullYear();
  const years = getYearsArray(2022, currentYear);

  return (
    <div className="journal-table">
      <Typography variant="h5" component="h2" gutterBottom>
        Member Journal Table
      </Typography>
      <MDBox
        display="flex"
        flexDirection="row"
        alignItems="center"
        sx={{
          width: '100%',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '8px 0',
          '& > *': {
            flex: '1 1 150px',
            minWidth: '120px',
            maxWidth: '200px',
          },
        }}
      >
        <MDBox>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Filter by Symbol</InputLabel>
            <Select
              value={filterBySymbol === '' ? 'All' : filterBySymbol}
              onChange={(e) => handleFilterBySymbol(e.target.value)}
              label="Filter by Symbol"
              sx={{ height: '36px' }}
            >
              <MenuItem value="All">
                <em>All</em>
              </MenuItem>
              {uniqueSymbols.map((symbol) => (
                <MenuItem key={symbol} value={symbol}>
                  {symbol}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
        <MDBox>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={filterByStatus === '' ? 'All' : filterByStatus}
              onChange={(e) => handleFilterByStatus(e.target.value)}
              label="Filter by Status"
              sx={{ height: '36px' }}
            >
              <MenuItem value="All">
                <em>All</em>
              </MenuItem>
              {uniqueStatus.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
        <MDBox>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Filter by Result</InputLabel>
            <Select
              value={filterByResult === '' ? 'All' : filterByResult}
              onChange={(e) => handleFilterByResult(e.target.value)}
              label="Filter by Result"
              sx={{ height: '36px' }}
            >
              <MenuItem value="All">
                <em>All</em>
              </MenuItem>
              <MenuItem value="wins">Wins</MenuItem>
              <MenuItem value="losses">Losses</MenuItem>
            </Select>
          </FormControl>
        </MDBox>
        <MDBox>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Filter by Confidence</InputLabel>
            <Select
              value={filterByConfidence === '' ? 'All' : filterByConfidence}
              onChange={(e) => handleFilterByConfidence(e.target.value)}
              label="Filter by Confidence"
              sx={{ height: '36px' }}
            >
              <MenuItem value="All">
                <em>All</em>
              </MenuItem>
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </Select>
          </FormControl>
        </MDBox>
        <MDBox>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Filter by Tags</InputLabel>
            <Select
              value={filterByTags === '' ? 'All' : filterByTags}
              onChange={(e) => handleFilterByTags(e.target.value)}
              label="Filter by Tags"
              sx={{ height: '36px' }}
            >
              <MenuItem value="All">
                <em>All</em>
              </MenuItem>
              {uniqueTags.map((tag) => (
                <MenuItem key={tag} value={tag}>
                  {tag}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
        <MDBox>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Filter by Trade Score</InputLabel>
            <Select
              value={filterByTradeScore}
              onChange={(e) => setFilterByTradeScore(e.target.value)}
              label="Filter by Trade Score"
              sx={{ height: '36px' }}
            >
              <MenuItem value="All">
                <em>All</em>
              </MenuItem>
              <MenuItem value="80-100">80-100</MenuItem>
              <MenuItem value="50-79">50-79</MenuItem>
              <MenuItem value="<50">{'<50'}</MenuItem>
            </Select>
          </FormControl>
        </MDBox>
        <MDBox>
          <TextField
            type="date"
            label="Filter by Date"
            value={filterByDate}
            onChange={(e) => handleFilterByDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ '& .MuiInputBase-root': { height: '36px' } }}
          />
        </MDBox>
        <MDBox>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Filter by Month</InputLabel>
            <Select
              value={filterByMonth}
              onChange={(e) => handleFilterByMonth(e.target.value)}
              label="Filter by Month"
              sx={{ height: '36px' }}
            >
              <MenuItem value="All">
                <em>All</em>
              </MenuItem>
              <MenuItem value="1">January</MenuItem>
              <MenuItem value="2">February</MenuItem>
              <MenuItem value="3">March</MenuItem>
              <MenuItem value="4">April</MenuItem>
              <MenuItem value="5">May</MenuItem>
              <MenuItem value="6">June</MenuItem>
              <MenuItem value="7">July</MenuItem>
              <MenuItem value="8">August</MenuItem>
              <MenuItem value="9">September</MenuItem>
              <MenuItem value="10">October</MenuItem>
              <MenuItem value="11">November</MenuItem>
              <MenuItem value="12">December</MenuItem>
            </Select>
          </FormControl>
        </MDBox>
        <MDBox>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Filter by Year</InputLabel>
            <Select
              value={filterByYear}
              onChange={(e) => handleFilterByYear(e.target.value)}
              label="Filter by Year"
              sx={{ height: '36px' }}
            >
              <MenuItem value="All">
                <em>All</em>
              </MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
      </MDBox>
      <table>
        <thead>
          <tr>
            <th
              style={{
                backgroundColor: theme.palette.background.default || '#bac7db',
                borderTopLeftRadius: '12px',
                borderBottomLeftRadius: '12px',
              }}
              onClick={() => handleSort('entryDateTime')}
            >
              <MDTypography variant="h6" component="span">
                Entry Date {renderSortIndicator('entryDateTime')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('exitDateTime')}>
              <MDTypography variant="h6" component="span">
                Exit Date {renderSortIndicator('exitDateTime')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('symbol')}>
              <MDTypography variant="h6" component="span">
                Symbol {renderSortIndicator('symbol')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('entry')}>
              <MDTypography variant="h6" component="span">
                Entry {renderSortIndicator('entry')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('exit')}>
              <MDTypography variant="h6" component="span">
                Exit {renderSortIndicator('exit')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('leverage')}>
              <MDTypography variant="h6" component="span">
                Leverage {renderSortIndicator('leverage')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('sizeType')}>
              <MDTypography variant="h6" component="span">
                Type {renderSortIndicator('sizeType')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('lotSizeEntry')}>
              <MDTypography variant="h6" component="span">
                Lot or<br />$ Value {renderSortIndicator('lotSizeEntry')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('shortLong')}>
              <MDTypography variant="h6" component="span">
                Short/Long {renderSortIndicator('shortLong')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('returns')}>
              <MDTypography variant="h6" component="span">
                Returns ($) {renderSortIndicator('returns')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('returnPercentage')}>
              <MDTypography variant="h6" component="span">
                Return % {renderSortIndicator('returnPercentage')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('riskRewardRatio')}>
              <MDTypography variant="h6" component="span">
                Targeted RR {renderSortIndicator('riskRewardRatio')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('actualRRRatio')}>
              <MDTypography variant="h6" component="span">
                Actual RR {renderSortIndicator('actualRRRatio')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('status')}>
              <MDTypography variant="h6" component="span">
                Status {renderSortIndicator('status')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('tradeConfidence')}>
              <MDTypography variant="h6" component="span">
                Confidence {renderSortIndicator('tradeConfidence')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('tags')}>
              <MDTypography variant="h6" component="span">
                Tags {renderSortIndicator('tags')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('screenshot')}>
              <MDTypography variant="h6" component="span">
                Screenshot {renderSortIndicator('screenshot')}
              </MDTypography>
            </th>
            <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }} onClick={() => handleSort('fxbTradeScore')}>
              <MDTypography variant="h6" component="span">
                Trade Score {renderSortIndicator('fxbTradeScore')}
              </MDTypography>
            </th>
            <th
              style={{
                backgroundColor: theme.palette.background.default || '#bac7db',
                borderTopRightRadius: '12px',
                borderBottomRightRadius: '12px',
              }}
            >
              <MDTypography variant="h6" onClick={(e) => e.stopPropagation()} component="span"></MDTypography>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedEntries().map((entry, index) => (
            <tr key={index} onClick={() => handleRowClick(entry)}>
              <td className="date-cell">
                <span>
                  <MDTypography variant="body3">
                    {entry.entryDateTime !== 'N/A' && !isNaN(new Date(entry.entryDateTime).getTime())
                      ? new Date(entry.entryDateTime).toLocaleDateString()
                      : 'N/A'}
                  </MDTypography>
                </span>
                <span>
                  <MDTypography variant="body3">
                    {entry.entryDateTime !== 'N/A' && !isNaN(new Date(entry.entryDateTime).getTime())
                      ? new Date(entry.entryDateTime).toLocaleTimeString()
                      : ''}
                  </MDTypography>
                </span>
              </td>
              <td className="date-cell">
                <span>
                  <MDTypography variant="body3">
                    {entry.exitDateTime !== 'N/A' && !isNaN(new Date(entry.exitDateTime).getTime())
                      ? new Date(entry.exitDateTime).toLocaleDateString()
                      : 'N/A'}
                  </MDTypography>
                </span>
                <span>
                  <MDTypography variant="body3">
                    {entry.exitDateTime !== 'N/A' && !isNaN(new Date(entry.exitDateTime).getTime())
                      ? new Date(entry.exitDateTime).toLocaleTimeString()
                      : ''}
                  </MDTypography>
                </span>
              </td>
              <td>
                <MDTypography variant="body3">{entry.symbol}</MDTypography>
              </td>
              <td>
                <MDTypography variant="body3">{entry.entry}</MDTypography>
              </td>
              <td>
                <MDTypography variant="body3">{entry.exit}</MDTypography>
              </td>
              <td>
                <MDTypography variant="body3">x{entry.leverage}</MDTypography>
              </td>
              <td>
                <MDTypography variant="body3">{entry.sizeType}</MDTypography>
              </td>
              <td>
                <MDTypography variant="body3">
                  {entry.sizeType === 'lot' ? entry.lotSizeEntry : entry.sizeAmount ? `$${parseFloat(entry.sizeAmount).toFixed(2)}` : 'N/A'}
                </MDTypography>
              </td>
              <td>
                <MDTypography variant="body3">{entry.shortLong}</MDTypography>
              </td>
              <td className={`return ${entry.returns > 0 ? 'positive' : entry.returns < 0 ? 'negative' : 'breakeven'}`}>
                ${!isNaN(parseFloat(entry.returns)) ? parseFloat(entry.returns).toFixed(2) : 'N/A'}
              </td>
              <td>
                <MDTypography variant="body3">{entry.returnPercentage ? `${entry.returnPercentage.toFixed(2)}%` : 'N/A'}</MDTypography>
              </td>
              <td>
                <MDTypography variant="body3">{entry.riskRewardRatio.toFixed(2)}</MDTypography>
              </td>
              <td>
                <MDTypography variant="body3">{entry.actualRRRatio}</MDTypography>
              </td>
              <td>
                <MDTypography variant="body3">{entry.status}</MDTypography>
              </td>
              <td>{getConfidenceIcon(entry.tradeConfidence)}</td>
              <td>
                <MDTypography variant="body3">{Array.isArray(entry.tags) && entry.tags.length > 0 ? entry.tags.join(', ') : 'N/A'}</MDTypography>
              </td>
              <td>
                {entry.screenshot ? (
                  <img src={entry.screenshot} alt="Screenshot" style={{ width: '100px', height: 'auto' }} />
                ) : (
                  <MDTypography variant="body3">No Screenshot</MDTypography>
                )}
              </td>
              <td>{renderTradeScore(entry.fxbTradeScore)}</td>
              <td>
                <div onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuOpen(e, entry);
                    }}
                    style={{ color: iconColor }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleMenuClose}>
                    <Tooltip
                      title={
                        !isReplayAvailable(selectedTrade)
                          ? "Trade Replay is available 24 hours after the trade's exit to process optimal entry, exit, stop loss, and take profit data."
                          : ''
                      }
                      placement="left"
                      arrow
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          fontSize: '0.75rem',
                          borderRadius: '4px',
                          boxShadow: theme.shadows[3],
                        },
                        '& .MuiTooltip-arrow': {
                          color: theme.palette.background.paper,
                        },
                      }}
                    >
                      <span>
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/fx-dashboard/trade-replay/${selectedTrade.id}`, { state: selectedTrade });
                            console.log('Navigating with selectedTrade:', selectedTrade);
                            handleMenuClose();
                          }}
                          disabled={!isReplayAvailable(selectedTrade)}
                        >
                          <WaterfallChartIcon style={{ marginRight: 8 }} /> Go to Trade Replay
                        </MenuItem>
                      </span>
                    </Tooltip>
                  </Menu>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {isModalOpen && selectedTrade && (
        <Paper
          style={{
            padding: 16,
            marginRight: '15px',
            backgroundColor: theme.palette.background.default || '#bac7db',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
          }}
          className="modal-journal-image"
        >
          <div
            className="modal-content-journal-image"
            style={{ display: 'flex', width: '80%', position: 'relative', maxHeight: '85vh', overflowY: 'auto' }}
          >
            <div style={{ flex: 0.5, paddingRight: '20px' }}>
              <MDTypography variant="h4">Selected Trade Entry</MDTypography>
              <MDTypography variant="body2">
                Entry Date and Time:{' '}
                {selectedTrade.entryDateTime !== 'N/A' ? new Date(selectedTrade.entryDateTime).toLocaleString() : 'N/A'}
              </MDTypography>
              <MDTypography variant="body2">
                Exit Date and Time:{' '}
                {selectedTrade.exitDate ? new Date(selectedTrade.exitDate + 'T' + (selectedTrade.exitTime || '00:00')).toLocaleString() : 'N/A'}
              </MDTypography>
              <MDTypography variant="body2">
                Hold Time: {calculateHoldTime(selectedTrade.entryDateTime, selectedTrade.exitDate, selectedTrade.exitTime)}
              </MDTypography>
              <MDTypography variant="body2">Symbol: {selectedTrade.symbol}</MDTypography>
              <MDTypography variant="body2">Entry: {selectedTrade.entry}</MDTypography>
              <MDTypography variant="body2">Exit: {selectedTrade.exit}</MDTypography>
              <MDTypography variant="body2">SL: {selectedTrade.sl}</MDTypography>
              <MDTypography variant="body2">TP: {selectedTrade.tp}</MDTypography>
              <MDTypography variant="body2">
                Pips: {Math.abs((parseFloat(selectedTrade.entry) - parseFloat(selectedTrade.exit)) / calculatePipIncrement(selectedTrade.symbol)).toFixed(0)}
              </MDTypography>
              <MDTypography variant="body2">Leverage: {selectedTrade.leverage}</MDTypography>
              <MDTypography variant="body2">Size Type: {selectedTrade.sizeType}</MDTypography>
              <MDBox display="flex" flexDirection="row">
                <MDTypography variant="body2">Lot Size / $ Amount:</MDTypography>
                <MDTypography variant="body2" style={{ marginLeft: '4px' }}>
                  {selectedTrade.sizeType === 'lot'
                    ? selectedTrade.lotSizeEntry
                    : selectedTrade.sizeAmount
                    ? `$${parseFloat(selectedTrade.sizeAmount).toFixed(2)}`
                    : 'N/A'}
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2">Short/Long: {selectedTrade.shortLong}</MDTypography>
              <MDTypography variant="body2">Returns $: {typeof selectedTrade.returns === 'number' && !isNaN(selectedTrade.returns) ? selectedTrade.returns.toFixed(2) : 'N/A'}</MDTypography>
              <MDTypography variant="body2">
  Return %: {typeof selectedTrade.returnPercentage === 'number' && !isNaN(selectedTrade.returnPercentage) ? selectedTrade.returnPercentage.toFixed(2) : 'N/A'}
</MDTypography>
<MDTypography variant="body2">
  Risk Amount $: {typeof selectedTrade.riskAmount === 'number' && !isNaN(selectedTrade.riskAmount) ? selectedTrade.riskAmount.toFixed(2) : 'N/A'}
</MDTypography>
              <MDTypography variant="body2">Targeted RR: {selectedTrade.riskRewardRatio}</MDTypography>
              <MDTypography variant="body2">Actual RR: {selectedTrade.actualRRRatio}</MDTypography>
              <MDTypography variant="body2">Status: {selectedTrade.status}</MDTypography>
              <MDTypography variant="body2">Trade Confidence: {getConfidenceIcon(selectedTrade.tradeConfidence)}</MDTypography>
              <MDTypography variant="body2">Trade Score: {renderTradeScore(selectedTrade.fxbTradeScore)}</MDTypography>
              <MDTypography variant="body2">
                Tags: {Array.isArray(selectedTrade.tags) && selectedTrade.tags.length > 0 ? selectedTrade.tags.join(', ') : 'N/A'}
              </MDTypography>
              <MDTypography variant="body2">
  Commission Fee: {typeof selectedTrade.commission === 'number' && !isNaN(selectedTrade.commission) ? selectedTrade.commission.toFixed(2) : 'N/A'}
</MDTypography>
              <MDTypography variant="body2">Comments: {selectedTrade.comments}</MDTypography>
            </div>
            <div style={{ flex: 2.5 }}>
              <span className="close" onClick={closeModal}>
                ×
              </span>
              {selectedTrade.screenshot && <img src={selectedTrade.screenshot} alt="Screenshot" style={{ width: '100%' }} />}
              {!selectedTrade.screenshot && <MDTypography variant="body2">No Screenshot Available</MDTypography>}
            </div>
          </div>
        </Paper>
      )}
    </div>
  );
};

TeamJournalTable.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

export default TeamJournalTable;